import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {GtAuthenticationService} from '@app/services/gt-authentication.service';
import {checkResponse} from '@app/shared/utils/app-utils';
import {DirectoryService, NameValueService, Organization} from '@gt-api';
import {forkJoin, of} from 'rxjs';
import {switchMap, tap} from 'rxjs/operators';
import {environment} from '@environment';

export interface OrganizationDialogData {
  organization?: Organization;
  providers?: string[];
  inboundServices?: string[];
  outboundServices?: string[];
  editMode?: boolean;
}

@Component({
  selector: 'gt-organization-editor',
  templateUrl: './organization-editor.component.html',
  styleUrls: ['./organization-editor.component.scss']
})
export class OrganizationEditorComponent implements OnInit {
  organization: Organization;
  providers: string[];
  inboundServices: string[];
  outboundServices: string[];
  editMode = false;
  hasNumberLookup = false;
  hasSwitchboard = false;
  hasServiceProviderMicrosoftSync = environment.hasServiceProviderMicrosoftSync;
  hasServiceProviderNumberLookup = environment.hasServiceProviderNumberLookup;
  hasMicrosoftSync = false;
  ctiPushUrl: string;
  hasVipList = false;
  hasWallboard = false;
  hasConferenceReservation = false;
  hasConferenceAdHoc = false;
  hasServiceProviderCtiPush = environment.hasServiceProviderCtiPush;
  hasServiceProviderCompanyFeatureToggle = environment.hasServiceProviderCompanyFeatureToggle;

  constructor(
    private dialogRef: MatDialogRef<OrganizationEditorComponent>,
    @Inject(MAT_DIALOG_DATA) public dialogData: OrganizationDialogData,
    private directoryService: DirectoryService,
    private nameValueService: NameValueService,
    private authenticationService: GtAuthenticationService
  ) {}

  ngOnInit() {
    if (this.dialogData) {
      this.organization = this.dialogData.organization;
      this.providers = [null, ...this.dialogData.providers.filter(p => p !== this.organization.id)];
      this.inboundServices = this.dialogData.inboundServices;
      this.outboundServices = this.dialogData.outboundServices;
      this.editMode = this.dialogData.editMode;
    }
    if (this.editMode) {
      this.loadOrganizationFeatures();
    }
  }

  private loadOrganizationFeatures() {
    const productProfile$ = this.nameValueService.getDepartmentNameValue1('productProfile', this.organization.id, 0);
    const ctiPushUrl$ = this.nameValueService.getDepartmentNameValue1('CTIPushUrl', this.organization.id, 0);
    const swbExc$ = this.nameValueService.getDepartmentNameValue1('SWB_EXC', this.organization.id, 0);
    const numberLookup$ = this.nameValueService.getDepartmentNameValue1('NumberLookup', this.organization.id, 0);
    const conferenceService$ = this.nameValueService.getDepartmentNameValue1('ConferenceService', this.organization.id, 0);
    const conferenceServiceOutbound$ = this.nameValueService.getDepartmentNameValue1('ConferenceServiceOutbound', this.organization.id, 0);
    const vipQueue$ = this.nameValueService.getDepartmentNameValue1('VipQueue', this.organization.id, 0);
    const wallboardService$ = this.nameValueService.getDepartmentNameValue1('WallboardService', this.organization.id, 0);

    forkJoin([
      productProfile$,
      ctiPushUrl$,
      swbExc$,
      numberLookup$,
      conferenceService$,
      conferenceServiceOutbound$,
      vipQueue$,
      wallboardService$
    ])
      .pipe(
        tap(
          ([
            productProfile,
            ctiPushUrl,
            swbExc,
            numberLookup,
            conferenceService,
            conferenceServiceOutbound,
            vipQueue,
            wallboardService
          ]) => {
            this.hasSwitchboard = productProfile.value === 'MPBX';
            this.ctiPushUrl = ctiPushUrl.value ?? '';
            this.hasMicrosoftSync = swbExc.value === '1';
            this.hasNumberLookup = numberLookup.value === '1';
            this.hasConferenceReservation = conferenceService.value === '1';
            this.hasConferenceAdHoc = conferenceServiceOutbound.value === '1';
            this.hasVipList = vipQueue.value === '1';
            this.hasWallboard = wallboardService.value === '1';
          }
        )
      )
      .subscribe();
  }

  saveCompanySettings(): void {
    const setDepNameValue = (name, value, oname, depId) => this.nameValueService.setDepartmentNameValue1(name, value, oname, depId);
    const setCtiPushProfile$ = this.ctiPushUrl ? setDepNameValue('CTIPushUrl', this.ctiPushUrl, this.organization.id, 0) : of(null);
    const setProductProfile$ = setDepNameValue('productProfile', this.hasSwitchboard ? 'MPBX' : 'none', this.organization.id, 0);
    const setMicrosoftSync$ = setDepNameValue('SWB_EXC', this.hasMicrosoftSync ? '1' : '0', this.organization.id, 0);
    const setNumberLookup$ = setDepNameValue('NumberLookup', this.hasNumberLookup ? '1' : '0', this.organization.id, 0);
    const setConfService$ = setDepNameValue('ConferenceService', this.hasConferenceReservation ? '1' : '0', this.organization.id, 0);
    const setConfServiceOut$ = setDepNameValue('ConferenceServiceOutbound', this.hasConferenceAdHoc ? '1' : '0', this.organization.id, 0);
    const setVipList$ = setDepNameValue('VipQueue', this.hasVipList ? '1' : '0', this.organization.id, 0);
    const setWallboardService$ = setDepNameValue('WallboardService', this.hasWallboard ? '1' : '0', this.organization.id, 0);

    if (this.editMode) {
      this.directoryService
        .editOrganization1(this.authenticationService.oname, this.organization)
        .pipe(
          checkResponse(),
          switchMap(_ =>
            forkJoin([
              setCtiPushProfile$,
              setProductProfile$,
              setMicrosoftSync$,
              setNumberLookup$,
              setConfService$,
              setConfServiceOut$,
              setVipList$,
              setWallboardService$
            ])
          )
        )
        .subscribe(
          () => this.dialogRef.close(true),
          error => console.log('edit organization', error)
        );
    } else {
      this.directoryService
        .createOrganization1(this.authenticationService.oname, this.organization)
        .pipe(
          checkResponse(),
          switchMap(_ =>
            forkJoin([
              setCtiPushProfile$,
              setProductProfile$,
              setMicrosoftSync$,
              setNumberLookup$,
              setConfService$,
              setConfServiceOut$,
              setVipList$,
              setWallboardService$
            ])
          )
        )
        .subscribe(
          () => this.dialogRef.close(true),
          error => console.log('create organization', error)
        );
    }
  }

  onCancelClick(): void {
    this.dialogRef.close();
  }
}
